import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Upload, FileSpreadsheet, Calculator, Pencil, CheckCircle2, Copy,
  BarChart3, TrendingUp, Shield, Brain, FileDown, HelpCircle, AlertCircle
} from 'lucide-react';

const Section = ({ icon: Icon, title, children, color = 'indigo' }) => (
  <Card className="bg-zinc-900/60 border-zinc-800/60">
    <CardHeader className="pb-3">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg bg-${color}-500/10 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 text-${color}-400`} />
        </div>
        <CardTitle className="text-sm font-medium text-zinc-200">{title}</CardTitle>
      </div>
    </CardHeader>
    <CardContent className="text-sm text-zinc-400 space-y-3">
      {children}
    </CardContent>
  </Card>
);

const ManualFieldIndicator = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-medium">
    <Pencil className="w-3 h-3" /> Manual Entry
  </span>
);

export default function UserGuidePage() {
  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-guide-page">
      <div>
        <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">User Guide</h2>
        <p className="text-sm text-zinc-400 mt-1">How to use AcquiFlow for business acquisition analysis</p>
      </div>

      {/* Quick Start */}
      <Card className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-indigo-500/20">
        <CardContent className="p-5">
          <h3 className="text-base font-semibold text-zinc-100 mb-3 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-indigo-400" />
            Quick Start Guide
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-300">
            <li><strong>Import Data</strong> — Upload an Excel file with P&L, balance sheet, and deal terms</li>
            <li><strong>Review & Adjust</strong> — Use Manual Entry forms to refine data</li>
            <li><strong>Analyze</strong> — Work through Stage 1 (financials) → Stage 2 (valuation)</li>
            <li><strong>Generate Report</strong> — Export comprehensive PDF with all findings</li>
            <li><strong>Mark Complete</strong> — Archive analysis and start fresh when ready</li>
          </ol>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Data Import */}
        <Section icon={Upload} title="1. Data Import" color="emerald">
          <p>Start by uploading an Excel file (.xlsx) containing your target company's financial data. The system expects:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Deal Tab</strong> — Purchase price, working capital, SDE, financing terms</li>
            <li><strong>P&L Tab</strong> — 2-5 years of revenue, COGS, operating expenses</li>
            <li><strong>Balance Sheet Tab</strong> — Assets, liabilities, equity</li>
            <li><strong>SDE Tab</strong> — Add-backs (officer salary, depreciation, etc.)</li>
          </ul>
          <div className="flex items-center gap-2 mt-3 p-2 rounded bg-zinc-800/50">
            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="text-xs text-zinc-400">Missing data? Use Manual Entry to fill gaps after upload.</span>
          </div>
        </Section>

        {/* Manual Entry */}
        <Section icon={Pencil} title="2. Manual Entry Fields" color="amber">
          <p>Fields marked with <ManualFieldIndicator /> can be manually edited to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Override imported values with verified data</li>
            <li>Add missing information not in the Excel file</li>
            <li>Test different scenarios by adjusting assumptions</li>
          </ul>
          <div className="mt-3 space-y-2">
            <p className="text-zinc-300 font-medium text-xs">Manual Entry Tabs:</p>
            <div className="flex flex-wrap gap-1.5">
              {['Deal Terms', 'P&L Data', 'Balance Sheet', 'SDE Add-backs', 'DCF Assumptions', 'Working Capital', 'Scenarios'].map(tab => (
                <Badge key={tab} variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]">{tab}</Badge>
              ))}
            </div>
          </div>
        </Section>

        {/* Stage 1 Analysis */}
        <Section icon={Shield} title="3. Stage 1: Financial Analysis" color="emerald">
          <p>Stage 1 examines the historical financial health of the target:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Deal Comparison</strong> — Seller vs Buyer vs Comparable terms</li>
            <li><strong>SDE Calculator</strong> — True owner earnings via add-back waterfall</li>
            <li><strong>P&L Summary</strong> — Revenue trends, margins, YoY growth</li>
            <li><strong>Solvency</strong> — Current ratio, D/E, DSCR analysis</li>
            <li><strong>Inventory</strong> — Turnover efficiency metrics</li>
            <li><strong>Working Capital</strong> — Normalization, peg/collar, true-up</li>
          </ul>
        </Section>

        {/* Stage 2 Analysis */}
        <Section icon={TrendingUp} title="4. Stage 2: Valuation & Scenarios" color="amber">
          <p>Stage 2 projects future value and tests assumptions:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>DCF Valuation</strong> — 5-year projections with adjustable sliders</li>
            <li><strong>Scenario Analysis</strong> — Flat, growth, and decline cases</li>
            <li><strong>Sensitivity</strong> — Tornado charts + Monte Carlo simulations</li>
            <li><strong>Key Findings</strong> — AI-powered summary of red flags & insights</li>
          </ul>
        </Section>

        {/* Completing Analysis */}
        <Section icon={CheckCircle2} title="5. Completing an Analysis" color="indigo">
          <p>When you're finished analyzing a deal:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Generate and download the full PDF report</li>
            <li>Click <strong>"Mark Complete"</strong> on the Dashboard</li>
            <li>The analysis is archived with a <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-[10px] mx-1">Complete</Badge> status</li>
            <li>You can reopen it later if adjustments are needed</li>
          </ol>
          <div className="flex items-center gap-2 mt-3 p-2 rounded bg-zinc-800/50">
            <AlertCircle className="w-4 h-4 text-indigo-400 shrink-0" />
            <span className="text-xs text-zinc-400">"Complete" is a status marker — it doesn't lock the analysis.</span>
          </div>
        </Section>

        {/* Starting New Analysis */}
        <Section icon={Copy} title="6. Starting a New Analysis" color="purple">
          <p>To begin a new deal analysis:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Fresh Start</strong> — Click "+ New" on the Dashboard</li>
            <li><strong>From Template</strong> — Duplicate a completed analysis as a starting point</li>
          </ul>
          <p className="mt-2">Duplicating preserves all settings and structure while creating a separate copy you can modify freely.</p>
        </Section>
      </div>

      {/* Manual Entry Legend */}
      <Card className="bg-zinc-900/60 border-amber-500/20">
        <CardContent className="p-4">
          <h4 className="text-sm font-medium text-zinc-200 mb-3 flex items-center gap-2">
            <Pencil className="w-4 h-4 text-amber-400" />
            Manual Entry Field Indicators
          </h4>
          <p className="text-xs text-zinc-400 mb-3">
            Throughout the application, editable fields are marked with visual indicators:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex items-center gap-2 p-2 rounded bg-zinc-800/50">
              <div className="w-3 h-3 rounded border-2 border-amber-500/50 bg-amber-500/10" />
              <span className="text-xs text-zinc-300">Amber border = editable field</span>
            </div>
            <div className="flex items-center gap-2 p-2 rounded bg-zinc-800/50">
              <Pencil className="w-3 h-3 text-amber-400" />
              <span className="text-xs text-zinc-300">Pencil icon = manual entry</span>
            </div>
            <div className="flex items-center gap-2 p-2 rounded bg-zinc-800/50">
              <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-[10px]">Editable</Badge>
              <span className="text-xs text-zinc-300">Badge label indicator</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Excel Upload Format Specification */}
      <Card className="bg-gradient-to-r from-emerald-500/5 to-teal-500/5 border-emerald-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-zinc-100 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
            Excel Upload Format Specification
          </CardTitle>
          <p className="text-xs text-zinc-400 mt-1">Required sheet names, cell locations, and data format for optimal import</p>
        </CardHeader>
        <CardContent className="space-y-5">
          
          {/* Sheet 1: Deal Scenario (Seller) */}
          <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
            <h5 className="text-sm font-semibold text-emerald-400 mb-2">Sheet 1: "Deal Scenario (Seller)"</h5>
            <p className="text-xs text-zinc-400 mb-3">Seller's asking price and proposed deal structure</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left py-1.5 text-zinc-400 font-medium w-16">Cell</th>
                    <th className="text-left py-1.5 text-zinc-400 font-medium">Field</th>
                    <th className="text-left py-1.5 text-zinc-400 font-medium w-28">Format</th>
                    <th className="text-left py-1.5 text-zinc-400 font-medium">Example</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300">
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B2</td><td>Purchase Price</td><td>Number ($)</td><td>1500000</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B3</td><td>Working Capital</td><td>Number ($)</td><td>200000</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B4</td><td>Closing Fees</td><td>Number ($)</td><td>25000</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B7</td><td>SDE</td><td>Number ($)</td><td>450000</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B9</td><td>Buyer Salary</td><td>Number ($)</td><td>75000</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B11</td><td>SBA Loan %</td><td>Decimal (0-1)</td><td>0.00</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B13</td><td>SBA Rate</td><td>Decimal</td><td>0.10</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B14</td><td>SBA Years</td><td>Number</td><td>10</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B16</td><td>Seller Note %</td><td>Decimal (0-1)</td><td>0.60</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B18</td><td>Seller Note Rate</td><td>Decimal</td><td>0.06</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-emerald-400">B19</td><td>Seller Note Years</td><td>Number</td><td>5</td></tr>
                  <tr><td className="py-1.5 font-mono text-emerald-400">B21</td><td>Equity %</td><td>Decimal (0-1)</td><td>0.40</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Sheet 2: Planned Offer (Buyer) */}
          <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
            <h5 className="text-sm font-semibold text-indigo-400 mb-2">Sheet 2: "Planned Offer (Buyer)"</h5>
            <p className="text-xs text-zinc-400 mb-3">Buyer's proposed offer — same cell layout as Seller sheet</p>
            <div className="text-xs text-zinc-300 bg-zinc-900/40 p-2 rounded font-mono">
              Same structure as "Deal Scenario (Seller)" — cells B2-B21
            </div>
          </div>

          {/* Sheet 3: Comparable */}
          <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
            <h5 className="text-sm font-semibold text-purple-400 mb-2">Sheet 3: "Cabinet IQ (Comp)"</h5>
            <p className="text-xs text-zinc-400 mb-3">Comparable company/deal for benchmarking — same cell layout</p>
            <div className="text-xs text-zinc-300 bg-zinc-900/40 p-2 rounded font-mono">
              Same structure as "Deal Scenario (Seller)" — cells B2-B21
            </div>
          </div>

          {/* Sheet 4: P&L */}
          <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
            <h5 className="text-sm font-semibold text-amber-400 mb-2">Sheet 4: "PnL (2021-2025)"</h5>
            <p className="text-xs text-zinc-400 mb-3">2-5 years of P&L data in columns</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left py-1.5 text-zinc-400 font-medium w-20">Row</th>
                    <th className="text-left py-1.5 text-zinc-400 font-medium">Field</th>
                    <th className="text-left py-1.5 text-zinc-400 font-medium">Col B (2019)</th>
                    <th className="text-left py-1.5 text-zinc-400 font-medium">Col D (2021)</th>
                    <th className="text-left py-1.5 text-zinc-400 font-medium">Col E (2022)</th>
                    <th className="text-left py-1.5 text-zinc-400 font-medium">...</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300">
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-amber-400">Row 4</td><td>Revenue</td><td>3500000</td><td>3800000</td><td>4200000</td><td>...</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-amber-400">Row 5</td><td>COGS</td><td>-2100000</td><td>-2280000</td><td>-2520000</td><td>...</td></tr>
                  <tr className="border-b border-zinc-800/50"><td className="py-1.5 font-mono text-amber-400">Row 7</td><td>Operating Expenses</td><td>-800000</td><td>-850000</td><td>-900000</td><td>...</td></tr>
                  <tr><td className="py-1.5 font-mono text-amber-400">Row 9</td><td>Net Income</td><td>600000</td><td>670000</td><td>780000</td><td>...</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">* COGS should be negative values. Years in columns B, D, E, F, G, H.</p>
          </div>

          {/* Sheet 5: SDE & Balance Sheet */}
          <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
            <h5 className="text-sm font-semibold text-cyan-400 mb-2">Sheet 5: "Buyer FS Analysis"</h5>
            <p className="text-xs text-zinc-400 mb-3">SDE add-backs and balance sheet data</p>
            
            <div className="space-y-4">
              {/* SDE Section */}
              <div>
                <h6 className="text-xs font-medium text-zinc-300 mb-2">SDE Add-backs (Rows 19-24)</h6>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-700">
                        <th className="text-left py-1.5 text-zinc-400 font-medium w-20">Row</th>
                        <th className="text-left py-1.5 text-zinc-400 font-medium">Field</th>
                        <th className="text-left py-1.5 text-zinc-400 font-medium">Col B (2024)</th>
                        <th className="text-left py-1.5 text-zinc-400 font-medium">Col C (2025)</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 19</td><td>Net Income</td><td>350000</td><td>380000</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 20</td><td>Officer Salary</td><td>120000</td><td>125000</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 21</td><td>Interest Expense</td><td>15000</td><td>12000</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 22</td><td>Depreciation</td><td>45000</td><td>48000</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 23</td><td>Entertainment</td><td>8000</td><td>9000</td></tr>
                      <tr><td className="py-1 font-mono text-cyan-400">Row 24</td><td>Other Add-backs</td><td>12000</td><td>11000</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Balance Sheet Section */}
              <div>
                <h6 className="text-xs font-medium text-zinc-300 mb-2">Balance Sheet (Rows 49-63)</h6>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-700">
                        <th className="text-left py-1.5 text-zinc-400 font-medium w-20">Row</th>
                        <th className="text-left py-1.5 text-zinc-400 font-medium">Field</th>
                        <th className="text-left py-1.5 text-zinc-400 font-medium">Col B (Seller)</th>
                        <th className="text-left py-1.5 text-zinc-400 font-medium">Col C (Buyer)</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 49</td><td>Cash</td><td>50000</td><td>75000</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 50</td><td>Inventory</td><td>180000</td><td>180000</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 51</td><td>Accounts Receivable</td><td>220000</td><td>220000</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 55</td><td>Accounts Payable</td><td>95000</td><td>95000</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 56</td><td>Current Debt Portion</td><td>25000</td><td>45000</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 60</td><td>Seller Note Outstanding</td><td>0</td><td>900000</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 font-mono text-cyan-400">Row 61</td><td>SBA Loan Outstanding</td><td>0</td><td>0</td></tr>
                      <tr><td className="py-1 font-mono text-cyan-400">Row 63</td><td>Equity Injection</td><td>0</td><td>600000</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Sheet 6: DCF & Scenarios */}
          <div className="p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/50">
            <h5 className="text-sm font-semibold text-rose-400 mb-2">Sheet 6: "Buyer DCF & Scenario Analysis"</h5>
            <p className="text-xs text-zinc-400 mb-3">DCF assumptions and scenario growth rates</p>
            
            <div className="space-y-4">
              <div>
                <h6 className="text-xs font-medium text-zinc-300 mb-2">DCF Assumptions (Rows 3-13)</h6>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex justify-between p-1.5 bg-zinc-900/40 rounded"><span className="text-zinc-400">B3: Discount Rate</span><span className="text-zinc-300 font-mono">0.15</span></div>
                  <div className="flex justify-between p-1.5 bg-zinc-900/40 rounded"><span className="text-zinc-400">B4: Growth Rate</span><span className="text-zinc-300 font-mono">0.03</span></div>
                  <div className="flex justify-between p-1.5 bg-zinc-900/40 rounded"><span className="text-zinc-400">B5: Exit Multiple</span><span className="text-zinc-300 font-mono">3.0</span></div>
                  <div className="flex justify-between p-1.5 bg-zinc-900/40 rounded"><span className="text-zinc-400">B6: COGS %</span><span className="text-zinc-300 font-mono">0.6041</span></div>
                  <div className="flex justify-between p-1.5 bg-zinc-900/40 rounded"><span className="text-zinc-400">B7: OpEx %</span><span className="text-zinc-300 font-mono">0.2263</span></div>
                  <div className="flex justify-between p-1.5 bg-zinc-900/40 rounded"><span className="text-zinc-400">B8: Buyer Salary</span><span className="text-zinc-300 font-mono">75000</span></div>
                  <div className="flex justify-between p-1.5 bg-zinc-900/40 rounded"><span className="text-zinc-400">B9: Tax Rate</span><span className="text-zinc-300 font-mono">0.00</span></div>
                  <div className="flex justify-between p-1.5 bg-zinc-900/40 rounded"><span className="text-zinc-400">B13: Base Revenue</span><span className="text-zinc-300 font-mono">4500000</span></div>
                </div>
              </div>

              <div>
                <h6 className="text-xs font-medium text-zinc-300 mb-2">Scenarios (Rows 46-48, Columns B-G)</h6>
                <p className="text-[10px] text-zinc-500 mb-2">5-year growth rates + exit multiple per scenario</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-700">
                        <th className="text-left py-1 text-zinc-400 font-medium">Row</th>
                        <th className="text-left py-1 text-zinc-400 font-medium">Scenario</th>
                        <th className="text-center py-1 text-zinc-400 font-medium">B (Y1)</th>
                        <th className="text-center py-1 text-zinc-400 font-medium">C (Y2)</th>
                        <th className="text-center py-1 text-zinc-400 font-medium">D (Y3)</th>
                        <th className="text-center py-1 text-zinc-400 font-medium">E (Y4)</th>
                        <th className="text-center py-1 text-zinc-400 font-medium">F (Y5)</th>
                        <th className="text-center py-1 text-zinc-400 font-medium">G (Term)</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300 font-mono">
                      <tr className="border-b border-zinc-800/50"><td className="py-1 text-rose-400">46</td><td className="text-zinc-400">Flat</td><td className="text-center">0</td><td className="text-center">0</td><td className="text-center">0</td><td className="text-center">0</td><td className="text-center">0</td><td className="text-center">0.02</td></tr>
                      <tr className="border-b border-zinc-800/50"><td className="py-1 text-rose-400">47</td><td className="text-zinc-400">Recovery</td><td className="text-center">0.10</td><td className="text-center">0.10</td><td className="text-center">0.05</td><td className="text-center">0.05</td><td className="text-center">0.05</td><td className="text-center">0.02</td></tr>
                      <tr><td className="py-1 text-rose-400">48</td><td className="text-zinc-400">Decline</td><td className="text-center">-0.10</td><td className="text-center">-0.05</td><td className="text-center">0</td><td className="text-center">0</td><td className="text-center">0</td><td className="text-center">0.02</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Reference Summary */}
          <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
            <h5 className="text-sm font-semibold text-emerald-400 mb-3">Quick Reference: Required Sheets</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
              <div className="flex items-center gap-2 p-2 bg-zinc-800/40 rounded">
                <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-[9px]">1</Badge>
                <span className="text-zinc-300">Deal Scenario (Seller)</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-zinc-800/40 rounded">
                <Badge variant="outline" className="border-indigo-500/30 text-indigo-400 text-[9px]">2</Badge>
                <span className="text-zinc-300">Planned Offer (Buyer)</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-zinc-800/40 rounded">
                <Badge variant="outline" className="border-purple-500/30 text-purple-400 text-[9px]">3</Badge>
                <span className="text-zinc-300">Cabinet IQ (Comp)</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-zinc-800/40 rounded">
                <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-[9px]">4</Badge>
                <span className="text-zinc-300">PnL (2021-2025)</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-zinc-800/40 rounded">
                <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 text-[9px]">5</Badge>
                <span className="text-zinc-300">Buyer FS Analysis</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-zinc-800/40 rounded">
                <Badge variant="outline" className="border-rose-500/30 text-rose-400 text-[9px]">6</Badge>
                <span className="text-zinc-300">Buyer DCF & Scenario Analysis</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-200 flex items-center gap-2">
            <Brain className="w-4 h-4 text-indigo-400" />
            Pro Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-400">
            <li className="flex items-start gap-2">
              <span className="text-indigo-400 mt-0.5">•</span>
              Use <strong>Compare Deals</strong> to evaluate multiple acquisition targets side-by-side
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-400 mt-0.5">•</span>
              Run <strong>Monte Carlo</strong> simulations to understand valuation uncertainty
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-400 mt-0.5">•</span>
              Check the <strong>Deal Score</strong> on Dashboard for an at-a-glance quality rating
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-400 mt-0.5">•</span>
              Export individual sections as PDFs using the <strong>Export PDF</strong> buttons
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-400 mt-0.5">•</span>
              Share analyses with collaborators via the <strong>Share</strong> feature on Dashboard
            </li>
            <li className="flex items-start gap-2">
              <span className="text-indigo-400 mt-0.5">•</span>
              Review <strong>Key Findings</strong> for AI-generated insights and red flags
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
